import { useCallback } from 'react'
import { useSearchParams } from 'react-router'
import { isSortOption, type SortOption } from '@/catalog/selectors'

export const DEFAULT_SORT: SortOption = 'newest'

/**
 * Library search/filter/sort state, stored in the URL (`/?q=&genre=&sort=`)
 * so views are shareable and survive navigation. Shared by the header search
 * field and the library page.
 */
export function useLibraryQuery() {
  const [params, setParams] = useSearchParams()
  const sortParam = params.get('sort')

  const update = useCallback(
    (key: 'q' | 'genre' | 'sort', value: string | null) => {
      // Read the live URL rather than a render snapshot so rapid or delayed
      // updates (e.g. debounced search) never drop another parameter.
      const next = new URLSearchParams(window.location.search)
      if (value) next.set(key, value)
      else next.delete(key)
      setParams(next, { replace: true, preventScrollReset: true })
    },
    [setParams],
  )

  const clear = useCallback(() => setParams({}, { replace: true, preventScrollReset: true }), [setParams])

  return {
    search: params.get('q') ?? '',
    genre: params.get('genre'),
    sort: isSortOption(sortParam) ? sortParam : DEFAULT_SORT,
    update,
    clear,
  }
}
